import { useCallback, useEffect, useMemo, useState } from 'react';

import { ServiceType, ServiceTypeData } from './models';
import { createHookContext } from '@clover-util/react-context';
import { useLocalStorageState } from '@clover-util/use-storage-state';
import { useStorageExpiry } from 'hooks/useStorageExpiry';
import { useTableDetailsResource } from 'hooks/useApi/useTableDetailsResource';
import { useMerchant } from '../merchant';
import { useTranslation } from 'react-i18next';
import { ServiceOffering, TableDetails } from 'olo-service';
import { serviceTypeDataKey, serviceTypeKey, tableDetailsKey, getDefaultServiceType, getInitialServiceType, chosenServiceTypeKey, getSortedServices } from './util';
import { cartExpiryKey } from '../cart/util';
import { usePersistentQuery } from '../PersistentQueryParam';
import { useDelivery } from '../Delivery';
import { LocalStorage } from 'util/Storage';
import { noDeliveryAddress } from 'components/CartServices.util';

export const { useServiceType, ServiceTypeProvider } = createHookContext('ServiceType', () => {
  const { t } = useTranslation();
  const { merchant } = useMerchant();
  const queryParams = usePersistentQuery();
  const { deliveryMetadata, deliveryAddress } = useDelivery();
  const merchantId = merchant?.merchantUuid;
  const services = merchant?.services;

  const { order_type: queryParamOrderType, employee_id: employeeUuid, table_id: tableUuid } = queryParams;

  const hasDineInQueryParam = useMemo(() => typeof queryParamOrderType === 'string' && queryParamOrderType.toLowerCase() === 'dine_in', [queryParamOrderType]);

  // Initialize Service Type
  const serviceTypes = useMemo(() => ((services && getSortedServices(services)) || [{ enabled: true, type: 'PICKUP' } as ServiceOffering])
    .filter(({ enabled, type }) => enabled && !!type && type !== ServiceType.DINE_IN)
    .map(({ type } : { type: string }) => ({
      name: type,
      label: t(`serviceTypes.${type.toLowerCase()}`)
    })),
  [services, t]);

  const defaultServiceType = getDefaultServiceType(serviceTypes);

  const initialServiceType = getInitialServiceType(hasDineInQueryParam, defaultServiceType);

  const [typeMetadata, updateTypeMetadata] = useLocalStorageState<ServiceTypeData>(merchantId && serviceTypeDataKey(merchantId), {});
  const [serviceType, updateServiceType] = useLocalStorageState<string | undefined>(merchantId && serviceTypeKey(merchantId), initialServiceType);
  const [chosenServiceType, setChosenServiceType] = useLocalStorageState<string | undefined>(merchantId && chosenServiceTypeKey(merchantId), initialServiceType);
  const [isExpired, updateExpiry] = useStorageExpiry(cartExpiryKey);
  const table = useTableDetailsResource(merchantId, tableUuid);

  const [tableDetails, updateTableDetails] = useLocalStorageState<TableDetails>(merchantId && tableDetailsKey(merchantId), {});

  const note = useMemo(() => services?.find(service => service.type === serviceType)?.note, [services, serviceType]);
  const isDineIn = useMemo(() => serviceType === ServiceType.DINE_IN, [serviceType]);
  const hasTableId = !!tableDetails.id;

  useEffect(() => {
    if (hasDineInQueryParam && !isDineIn) updateServiceType(ServiceType.DINE_IN);
    updateTypeMetadata(value => ({
      ...value,
      tableUuid: tableUuid ?? value.tableUuid,
      employeeUuid: employeeUuid ?? value.employeeUuid,
    }));

    if (table.data) updateTableDetails(table.data);
    updateExpiry();
  }, [hasDineInQueryParam, employeeUuid, tableUuid, isDineIn, table.data, updateExpiry, updateTypeMetadata, updateServiceType, updateTableDetails]);

  // Clear Storage when expired
  useEffect(() => {
    if (!isExpired) return;
    updateServiceType(initialServiceType);

    // These will be updated when they come in as query params, so we don't want to clear them
    if (!hasDineInQueryParam) {
      updateTypeMetadata({});
      updateTableDetails({});
    }
  }, [isExpired, initialServiceType, hasDineInQueryParam, updateTypeMetadata, updateServiceType, updateTableDetails]);

  const resetTypeMetadata = useCallback((type?: string, metaData?: ServiceTypeData) => {
    updateTypeMetadata(value => {
      if (isDineIn) return ({ employeeUuid: value.employeeUuid, tableUuid: value.tableUuid });
      // Default Contactless Delivery
      if (type === ServiceType.DELIVERY || chosenServiceType === ServiceType.DELIVERY) {
        if (!deliveryMetadata && !metaData) return ({ isContactlessDelivery: true });
        if (deliveryMetadata) return ({ ...deliveryMetadata });
        if (metaData) return ({ ...metaData });
      }

      return {};
    });
  }, [isDineIn, deliveryMetadata, chosenServiceType, updateTypeMetadata]);

  const resetServiceTypeData = useCallback(() => {
    resetTypeMetadata();
    if (!isDineIn) updateServiceType(defaultServiceType?.name);
  }, [defaultServiceType?.name, isDineIn, resetTypeMetadata, updateServiceType]);

  const update = useCallback((type: string, metadata?) => {
    if (type === serviceType) return;
    updateServiceType(type);
    resetTypeMetadata(type, metadata);
    updateExpiry();
    setChosenServiceType(type);
  }, [serviceType, updateServiceType, resetTypeMetadata, updateExpiry, setChosenServiceType]);

  /** In the below useEffect I am checking that if service type stored in local storage is not in the services.
   * i.e. the merchant has disabled the service then we should update the current servie type to the first value from the enabled services */
  useEffect(() => {
    const currentServiceTypeFromLocalStorage = LocalStorage.tryGetJSONItem(`colo-service-type_${merchantId}`);
    const serviceTypeNotInServices = currentServiceTypeFromLocalStorage && serviceTypes.length !== 0 && serviceTypes.findIndex((service) => service.name === currentServiceTypeFromLocalStorage) === -1;

    if (serviceTypeNotInServices && !hasDineInQueryParam && !isDineIn) {
      update(serviceTypes[0].name);
    }
  }, [serviceTypes, update, merchantId, hasDineInQueryParam, isDineIn]);

  const [isRedirectAddressModal, setRedirectFromAddressModal] = useState(true);

  const hasDeliveryAddress = useMemo(()=> serviceType && noDeliveryAddress(serviceType, deliveryAddress), [serviceType, deliveryAddress]);

  return {
    currentServiceType: serviceType,
    serviceTypes,
    update,
    isDineIn,
    typeMetadata,
    updateTypeMetadata,
    note,
    resetTypeMetadata,
    tableDetails,
    resetServiceTypeData,
    hasDineInQueryParam,
    hasTableId,
    chosenServiceType,
    setChosenServiceType,
    isDelivery: useMemo(() => serviceType === ServiceType.DELIVERY, [serviceType]),
    isDeliveryChosen: useMemo(() => chosenServiceType === ServiceType.DELIVERY, [chosenServiceType]),
    hasDeliveryAddress,
    setRedirectFromAddressModal,
    isRedirectAddressModal
  };
});
